import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { DeltaResult } from "../../lib/delta";
import type { IntelMemory } from "../../lib/intelligence/executive";
import { memoryPath } from "../../lib/intelligence/memory-path";
import { IconArrowDown, IconArrowUp, IconMinus } from "../icons";

/**
 * Card de indicador da Visão Executiva (piloto do visual novo). Mostra o número,
 * a variação como um selo colorido pelo que é BOM para a empresa (não pela
 * direção da seta) e um mini-gráfico com dados que a tela já recebe. Com
 * `memory`, o card inteiro abre a memória de cálculo do número.
 */
export function KpiTile({
  label, value, delta, deltaLabel, status, tone, visual, footnote, memory, loading,
}: {
  label: string;
  value: string;
  delta?: DeltaResult | null;
  /** ex.: "vs. set." */
  deltaLabel?: string | null;
  status?: "warning" | "critical" | null;
  /** tinge o fundo do card inteiro (ex.: KPI que É um alerta, não só mostra um número) — oposto de `status`, que só adiciona o selinho */
  tone?: "warning" | "critical" | null;
  visual?: ReactNode;
  footnote?: ReactNode;
  memory?: IntelMemory | null;
  loading?: boolean;
}) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-gray-600">{label}</p>
        {status && !loading && (
          <span className={`flex items-center gap-1.5 text-xs font-semibold ${status === "critical" ? "text-danger-600" : "text-warning-700"}`}>
            <span className={`w-2 h-2 rounded-full ${status === "critical" ? "bg-danger-600" : "bg-warning-500"}`} aria-hidden="true" />
            {status === "critical" ? "Crítico" : "Atenção"}
          </span>
        )}
      </div>

      <p className="font-display font-semibold text-2xl leading-tight text-gray-900 tabular-nums mt-0.5 truncate">
        {loading ? <span className="inline-block w-28 h-6 rounded bg-gray-100 animate-pulse align-middle" /> : value}
      </p>

      {!loading && delta && (delta.pct != null || delta.label) && (
        <p className="flex items-center gap-1.5 mt-1 text-xs text-gray-600">
          <DeltaChip delta={delta} />
          {deltaLabel}
        </p>
      )}

      {!loading && visual && <div className="mt-2.5">{visual}</div>}
      {!loading && footnote && <p className="text-[11px] text-gray-500 mt-2 leading-snug">{footnote}</p>}
    </>
  );

  // h-full: numa grade com cards de conteúdo diferente (um com sparkline +
  // delta, outro só com footnote), cada card do flex-col ficava do tamanho do
  // próprio conteúdo — a grade alinha a LINHA pela altura do mais alto, mas só
  // o card esticava até lá com h-full fica todo mundo com a mesma altura.
  const toneClasses = tone === "critical" ? "border-danger-200 bg-danger-50"
    : tone === "warning" ? "border-warning-400/40 bg-warning-50"
    : "border-surface-border bg-white";
  const shell = `group relative flex flex-col h-full rounded-xl border ${toneClasses} px-4 py-3.5 transition-colors`;
  if (memory) {
    return (
      <Link
        to={memoryPath(memory)}
        className={`${shell} hover:border-tm-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500`}
        title="Ver como este número foi calculado"
      >
        {body}
      </Link>
    );
  }
  return <div className={shell}>{body}</div>;
}

export function DeltaChip({ delta }: { delta: DeltaResult }) {
  const tone = delta.isGood == null
    ? "bg-gray-100 text-gray-700"
    : delta.isGood ? "bg-tm-50 text-tm-700" : "bg-danger-50 text-danger-600";
  const Icon = delta.trend === "up" ? IconArrowUp : delta.trend === "down" ? IconArrowDown : IconMinus;
  const text = delta.label ?? `${Math.abs(delta.pct ?? 0).toFixed(1).replace(".", ",")}%`;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 font-semibold tabular-nums ${tone}`}>
      <Icon className="w-3 h-3" />
      {text}
    </span>
  );
}

/**
 * Caminho suave (curva, não reta) passando por todos os pontos: usa uma
 * quadrática por segmento com ponto de controle no dado original e destino no
 * meio do próximo segmento — técnica leve de "smoothing" sem libs externas.
 * Termina com T no último ponto real (reflete o controle anterior) pra curva
 * não parar no meio do caminho antes do valor mais recente.
 */
export function smoothSegments(points: Array<{ x: number; y: number }>): string {
  let d = "";
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i]!, p1 = points[i + 1]!;
    const midX = (p0.x + p1.x) / 2;
    const midY = (p0.y + p1.y) / 2;
    d += ` Q${p0.x.toFixed(2)},${p0.y.toFixed(2)} ${midX.toFixed(2)},${midY.toFixed(2)}`;
  }
  const last = points[points.length - 1]!;
  d += ` T${last.x.toFixed(2)},${last.y.toFixed(2)}`;
  return d;
}

/**
 * Linha com área suave: série diária ou mensal. O ponto marca o valor mais
 * recente (hoje / mês atual). O ponto é HTML, não SVG, para não deformar com o
 * `preserveAspectRatio="none"` que estica a linha na largura do card.
 */
export function SparkLine({ values, label }: { values: number[]; label: string }) {
  const pts = values.filter((v) => Number.isFinite(v));
  if (pts.length < 2) return null;
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const span = max - min || 1;
  const X = (i: number) => (i / (pts.length - 1)) * 100;
  const Y = (v: number) => 38 - ((v - min) / span) * 32;
  const points = pts.map((v, i) => ({ x: X(i), y: Y(v) }));
  const segments = smoothSegments(points);
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const strokePath = `M${first.x.toFixed(2)},${first.y.toFixed(2)}${segments}`;
  const areaPath = `M${first.x.toFixed(2)},40 L${first.x.toFixed(2)},${first.y.toFixed(2)}${segments} L${last.x.toFixed(2)},40 Z`;

  return (
    <div className="relative h-8" role="img" aria-label={label}>
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible" aria-hidden="true">
        <path d={areaPath} fill="rgb(var(--tm-400) / 0.14)" />
        <path d={strokePath} fill="none" stroke="rgb(var(--tm-500))" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <span
        className="absolute w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-tm-600 ring-2 ring-white"
        style={{ left: "100%", top: `${(last.y / 40) * 100}%` }}
        aria-hidden="true"
      />
    </div>
  );
}

/** Barra de composição (ex.: a vencer × vencido), com legenda. */
export function SplitMeter({
  parts,
}: { parts: Array<{ label: string; value: number; display: string; tone: "brand" | "danger" | "muted" }> }) {
  const total = parts.reduce((a, p) => a + Math.max(0, p.value), 0) || 1;
  const fill = { brand: "bg-tm-400", danger: "bg-danger-500", muted: "bg-gray-200" };
  return (
    <div>
      <div className="flex h-2 rounded-full overflow-hidden bg-gray-100" role="img"
        aria-label={parts.map((p) => `${p.label}: ${p.display}`).join(", ")}>
        {parts.map((p) => (
          <span key={p.label} className={fill[p.tone]} style={{ width: `${(Math.max(0, p.value) / total) * 100}%` }} />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-600">
        {parts.map((p) => (
          <li key={p.label} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${fill[p.tone]}`} aria-hidden="true" />
            {p.label} <span className="font-semibold text-gray-900 tabular-nums">{p.display}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
